import Ressource, { type RessourceUpdated, RessourceSources, RessourceTypes } from '@/framework/ressource/classes/Ressource';
import HtmlAttrib from '@/generated/classes/HtmlAttrib';
import { Alerts } from "@/framework/base/helpers/Alerts";
import { reactive } from "vue";

export interface AdminEditRessource {
  (ressource: MapRessource, updated: RessourceUpdated): boolean;
}

export interface EditRessource {
  (ressource: MapRessource, updated: RessourceUpdated): Promise<boolean>;
}

export class AppRessource {
  // Catégorie de ressource
  public type: RessourceTypes;
  // Nom de la ressource
  public name: string;
  // libellé associé à la ressource
  public label: string;
  // placeholder associé à la ressource
  public placeholder: string;
}

export class MapRessource extends AppRessource {
  public id: number;
  public helpHtmlId: number;
  public updated: RessourceUpdated;
}

export class Ressources {
  private static mapServer = new Map<string, MapRessource>();
  private static mapClient = new Map<string, MapRessource>();
  private static isMobile = false;

  /**
   * Insère / met à jour une ressource côté serveur dans la map,
   * en garantissant qu'on garde toujours la même instance réactive.
   */
  private static upsertServer(ressource: MapRessource | Ressource): MapRessource {
    const key = ressource.type + ressource.name;
    const existing = this.mapServer.get(key);

    if (existing) {
      // On mute l'objet déjà réactif (deep reactive)
      Object.assign(existing, ressource);
      return existing;
    } else {
      // On crée une instance réactive et on la stocke
      const reactiveRes = reactive(ressource) as MapRessource;
      this.mapServer.set(key, reactiveRes);
      return reactiveRes;
    }
  }

  public static define(data: AppRessource[], duplicated = true): void {
    data.forEach(r => {
      const ressource = new Ressource();
      ressource.source = RessourceSources.CLIENT;
      ressource.type = r.type;
      ressource.name = r.name;
      ressource.label = r.label;
      ressource.placeholder = r.placeholder;

      const server = Ressources.mapServer.get(ressource.type + ressource.name);
      if (!server) {
        const client = Ressources.mapClient.get(ressource.type + ressource.name);
        if (client) {
          if (duplicated) {
            Alerts.debug('Ressource duplicated: ' + ressource.type + ' ' + r.name);
          }
        } else {
          // côté client, la réactivité n'est pas critique, on peut garder tel quel
          Ressources.mapClient.set(ressource.type + ressource.name, ressource as unknown as MapRessource);
        }
      }
    });
  }

  public static initServer(data: AppRessource[]): void {
    data.forEach(r => {
      const ressource = new Ressource();
      ressource.source = RessourceSources.CLIENT;
      ressource.type = r.type;
      ressource.name = r.name;
      ressource.label = r.label;
      ressource.placeholder = r.placeholder;
      this.set(ressource);
    });
  }

  public static download(isMobile: boolean): Promise<void> {
    return new Promise((resolve) => {
      this.isMobile = isMobile;
      Ressource.listAll(isMobile).then((ressources) => {
        ressources.forEach(r => {
          if (isMobile && r.labelMobile != '') {
            r.label = r.labelMobile;
          }
          const reactiveRes = this.upsertServer(r);
          if (reactiveRes.id == 0) {
            debugger;
          }
        });
        resolve();
      });
    });
  }

  public static set(ressource: Ressource): void {
    ressource.create().then((res) => {
      const reactiveRes = this.upsertServer(res);
      if (reactiveRes.id == 0) {
        debugger;
      }
    });
  }

  public static mapServerSet(ressource: Ressource): void {
    this.upsertServer(ressource);
  }

  public static update(ressource: Ressource): void {
    ressource.update().then((res) => {
      const reactiveRes = this.upsertServer(res);
      if (reactiveRes.id == 0) {
        debugger;
      }
    });
  }

  public static getById(id: number): MapRessource {
    let res = new MapRessource();
    Ressources.mapServer.forEach((ressource) => {
      if (ressource.id == id) {
        res = ressource;
      }
    });
    return res;
  }

  /**
   * Retourne toujours la même instance réactive pour une ressource (type+name),
   * sans recréer de proxy à chaque appel.
   */
  public static newGet(type: RessourceTypes, name: string, id = 0): MapRessource {
    const key = type + name;

    let res = Ressources.mapServer.get(key);

    if (!res) {
      const ressourceClient = Ressources.mapClient.get(key);
      if (ressourceClient) {
        // On "promote" la ressource client dans le mapServer réactif
        res = this.upsertServer(ressourceClient);
      } else {
        const newRessource = new Ressource();
        newRessource.source = RessourceSources.CLIENT;
        newRessource.type = type;
        newRessource.name = name;
        newRessource.label = 'not defined';
        newRessource.placeholder = 'not defined';
        if (id == 0) {
          Alerts.debug('Ressource : ' + type + ', ' + name + ' non définie');
        }
        res = this.upsertServer(newRessource);
      }
    }

    // res est déjà une instance réactive contenue dans mapServer
    return res!;
  }

  private static adminEditRessource: AdminEditRessource;

  public static setAdminEditRessource(adminEditRessource: AdminEditRessource) {
    Ressources.adminEditRessource = adminEditRessource;
  }

  public static editRessource(ressource: MapRessource, updated: RessourceUpdated): Promise<boolean> {
    return new Promise((resolve) => {
      Ressources.getRessource(ressource).then((res) => {
        resolve(Ressources.adminEditRessource(res, updated));
      });
    });
  }

  public static getRessource(ressource: MapRessource): Promise<MapRessource> {
    return new Promise((resolve) => {
      if (ressource.id == 0) {
        const sRessource = new Ressource();
        sRessource.source = RessourceSources.CLIENT;
        sRessource.type = ressource.type;
        sRessource.name = ressource.name;
        sRessource.label = ressource.label;
        sRessource.placeholder = ressource.placeholder;
        sRessource.create().then((res) => {
          const reactiveRes = this.upsertServer(res);
          resolve(reactiveRes);
        });
      } else {
        // newGet renvoie l'instance réactive déjà dans mapServer
        resolve(Ressources.newGet(ressource.type, ressource.name, ressource.id));
      }
    });
  }

  public static getHelp(id: number): Promise<HtmlAttrib> {
    return new Promise((resolve) => {
      HtmlAttrib.get(id).then((res) => {
        if (this.isMobile && res.htmlMobile != '' && res.htmlMobile != '<div></div>') {
          res.html = res.htmlMobile;
        }
        resolve(res);
      });
    });
  }
}
